/* Admin dashboard — live counts + recent activity from Firestore. */
import { Link } from "../../lib/router-compat.jsx";
import AdminShell from "../../components/shell/AdminShell.jsx";
import { Card, Icon } from "../../ui/index.js";
import { useAuth } from "../../auth/AuthProvider.jsx";
import { useLeads, useEnquiries, useDonations, usePosts, useEvents } from "../../data/hooks.js";
import { formatRelative, CURRENCY_FMT } from "../../utils/format.js";

function StatCard({ label, value, icon: Ico, href, tone }) {
  return (
    <Link to={href} className="stat-card" aria-label={`${value} ${label} — view list`}>
      <div className="row row--between" style={{ alignItems: "flex-start" }}>
        <div className="stat-card__label">{label}</div>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: tone === "primary" ? "var(--accent-pink-soft)" : "var(--bg-warm)", color: tone === "primary" ? "var(--primary)" : "var(--text-muted)", display: "grid", placeItems: "center" }}>
          <Ico size={14} />
        </div>
      </div>
      <div className="stat-card__value">{value}</div>
      <div className="stat-card__sub">View list <Icon.ArrowRight size={12} /></div>
    </Link>
  );
}

export default function AdminDashboardPage() {
  const { profile } = useAuth();
  const { leads } = useLeads();
  const { enquiries } = useEnquiries();
  const { donations } = useDonations();
  const { posts } = usePosts();
  const { events } = useEvents();

  const newLeads = leads.filter((l) => l.status === "new").length;
  const newEnquiries = enquiries.filter((e) => e.status === "new").length;
  const pledged = donations.filter((d) => d.status === "pledged").length;
  const drafts = posts.filter((p) => p.status === "draft").length;
  const upcoming = events.filter((e) => e.status === "upcoming").length;

  // Build a recent-activity feed by merging the latest inbound records.
  const activity = [
    ...leads.slice(0, 5).map((l) => ({ icon: Icon.Heart, color: "var(--primary)", at: l.submittedAt, href: `/admin/leads/${l.id}`, text: <><strong>{l.donorName}</strong> pledged {CURRENCY_FMT(l.intendedAmount, l.currency)}</> })),
    ...enquiries.slice(0, 5).map((e) => ({ icon: Icon.Inbox, color: "var(--accent-pink)", at: e.submittedAt, href: `/admin/enquiries/${e.id}`, text: <><strong>{e.name}</strong> sent an enquiry</> })),
    ...donations.filter((d) => d.status === "received").slice(0, 5).map((d) => ({ icon: Icon.Gift, color: "var(--success)", at: d.createdAt, href: "/admin/donations", text: <>Donation of <strong>{CURRENCY_FMT(d.amount, d.currency)}</strong> received</> })),
  ].sort((a, b) => {
    const ta = a.at?.toMillis ? a.at.toMillis() : 0;
    const tb = b.at?.toMillis ? b.at.toMillis() : 0;
    return tb - ta;
  }).slice(0, 6);

  return (
    <AdminShell>
      <div className="admin-main__header">
        <div>
          <h1 className="t-3xl">Welcome back{profile?.displayName ? `, ${profile.displayName.split(" ")[0]}` : ""}.</h1>
          <p>Here's what's happening today.</p>
        </div>
      </div>

      <div className="stat-cards-row" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 40 }}>
        <StatCard label="New donor leads" value={newLeads} icon={Icon.Users} href="/admin/leads" tone={newLeads > 0 ? "primary" : "neutral"} />
        <StatCard label="New enquiries" value={newEnquiries} icon={Icon.Inbox} href="/admin/enquiries" tone={newEnquiries > 0 ? "primary" : "neutral"} />
        <StatCard label="Upcoming events" value={upcoming} icon={Icon.Calendar} href="/admin/events" />
        <StatCard label="Draft posts" value={drafts} icon={Icon.FileText} href="/admin/posts" />
      </div>

      <div className="dashboard-split" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 32 }}>
        <Card>
          <div className="row row--between" style={{ marginBottom: 20 }}>
            <h2 className="t-2xl" style={{ margin: 0 }}>Recent activity</h2>
            <span className="t-xs muted">Latest</span>
          </div>
          {activity.length === 0 ? (
            <p className="t-sm muted">No activity yet.</p>
          ) : (
            <div className="stack-md" style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {activity.map((a, i) => {
                const Ico = a.icon;
                return (
                  <Link key={i} to={a.href} style={{ display: "flex", gap: 14, padding: "14px 0", borderBottom: i < activity.length - 1 ? "1px solid var(--border)" : 0, color: "var(--text)" }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--bg-warm)", color: a.color, display: "grid", placeItems: "center", flexShrink: 0 }}><Ico size={16} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="t-sm" style={{ lineHeight: 1.4 }}>{a.text}</div>
                      <div className="t-xs muted" style={{ marginTop: 2 }}>{formatRelative(a.at)}</div>
                    </div>
                    <Icon.ChevronRight size={16} style={{ color: "var(--text-muted)", marginTop: 10, flexShrink: 0 }} />
                  </Link>
                );
              })}
            </div>
          )}
        </Card>

        <Card>
          <h2 className="t-2xl" style={{ margin: 0, marginBottom: 4 }}>Quick actions</h2>
          <p className="t-sm muted" style={{ marginTop: 4, marginBottom: 16 }}>Jump back into the work.</p>
          <div className="stack-md" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Link to="/admin/events/new" className="btn btn--secondary btn--full" style={{ justifyContent: "flex-start" }}><Icon.Plus size={16} />Create an event</Link>
            <Link to="/admin/posts/new" className="btn btn--secondary btn--full" style={{ justifyContent: "flex-start" }}><Icon.Plus size={16} />Write a new post</Link>
            <Link to="/admin/groups" className="btn btn--secondary btn--full" style={{ justifyContent: "flex-start" }}><Icon.School size={16} />Review school groups</Link>
            <Link to="/admin/donations" className="btn btn--secondary btn--full" style={{ justifyContent: "flex-start" }}><Icon.Gift size={16} />Log a donation</Link>
          </div>
        </Card>
      </div>
    </AdminShell>
  );
}
